/**
 * DataTable - Reusable table component with sorting, pagination, and row actions
 * 
 * Features:
 * - Column sorting
 * - Pagination
 * - Row selection
 * - Row actions
 * - Loading states
 * - Empty states
 */

import React, { useState, useMemo } from 'react';

const DataTable = ({
  columns = [],
  data = [],
  loading = false,
  emptyMessage = 'No data available',
  pageSize = 10,
  sortable = true,
  selectable = false,
  onRowClick = null,
  onSelectionChange = null,
  rowActions = null,
  className = ''
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [selectedRows, setSelectedRows] = useState(new Set());

  // Sort data
  const sortedData = useMemo(() => {
    const safeData = Array.isArray(data) ? data : [];
    if (!sortConfig.key || !sortable) return safeData;

    return [...safeData].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue === bValue) return 0;
      
      const comparison = aValue < bValue ? -1 : 1;
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
  }, [data, sortConfig, sortable]);

  // Paginate data
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return sortedData.slice(startIndex, startIndex + pageSize);
  }, [sortedData, currentPage, pageSize]);

  const totalPages = Math.ceil(sortedData.length / pageSize);

  const handleSort = (key) => {
    if (!sortable) return;

    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRows(new Set(paginatedData.map((_, idx) => idx)));
    } else {
      setSelectedRows(new Set());
    }
    
    if (onSelectionChange) {
      onSelectionChange(e.target.checked ? paginatedData : []);
    }
  };

  const handleSelectRow = (index, row) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedRows(newSelected);
    
    if (onSelectionChange) {
      const selectedData = paginatedData.filter((_, idx) => newSelected.has(idx));
      onSelectionChange(selectedData);
    }
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return '⇅';
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  if (loading) {
    return (
      <div className={`admin-data-table ${className}`}>
        <div className="admin-data-table__loading">
          <div className="admin-spinner"></div>
          <p>Loading data...</p>
        </div>
      </div>
    );
  }

  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <div className={`admin-data-table ${className}`}>
        <div className="admin-data-table__empty">
          <p>{emptyMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`admin-data-table ${className}`}>
      <div className="admin-data-table__wrapper">
        <table className="admin-data-table__table">
          <thead className="admin-data-table__header">
            <tr>
              {selectable && (
                <th className="admin-data-table__cell admin-data-table__cell--checkbox">
                  <input
                    type="checkbox"
                    checked={selectedRows.size === paginatedData.length && paginatedData.length > 0}
                    onChange={handleSelectAll}
                    aria-label="Select all rows"
                  />
                </th>
              )}
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`admin-data-table__cell ${column.sortable !== false && sortable ? 'admin-data-table__cell--sortable' : ''}`}
                  onClick={() => column.sortable !== false && handleSort(column.key)}
                  style={{ width: column.width }}
                >
                  <div className="admin-data-table__header-content">
                    <span>{column.label}</span>
                    {column.sortable !== false && sortable && (
                      <span className="admin-data-table__sort-icon">
                        {getSortIcon(column.key)}
                      </span>
                    )}
                  </div>
                </th>
              ))}
              {rowActions && (
                <th className="admin-data-table__cell admin-data-table__cell--actions">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="admin-data-table__body">
            {paginatedData.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                className={`admin-data-table__row ${onRowClick ? 'admin-data-table__row--clickable' : ''} ${selectedRows.has(rowIndex) ? 'admin-data-table__row--selected' : ''}`}
                onClick={() => onRowClick && onRowClick(row)}
              >
                {selectable && (
                  <td className="admin-data-table__cell admin-data-table__cell--checkbox">
                    <input
                      type="checkbox"
                      checked={selectedRows.has(rowIndex)}
                      onChange={() => handleSelectRow(rowIndex, row)}
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`Select row ${rowIndex + 1}`}
                    />
                  </td>
                )}
                {columns.map((column) => (
                  <td key={column.key} className="admin-data-table__cell">
                    {column.render ? column.render(row, row[column.key]) : row[column.key]}
                  </td>
                ))}
                {rowActions && (
                  <td className="admin-data-table__cell admin-data-table__cell--actions">
                    <div className="admin-data-table__actions" onClick={(e) => e.stopPropagation()}>
                      {rowActions(row)}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="admin-data-table__pagination">
          <div className="admin-data-table__pagination-info">
            Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, sortedData.length)} of {sortedData.length} entries
          </div>
          <div className="admin-data-table__pagination-controls">
            <button
              className="admin-btn admin-btn--secondary admin-btn--sm"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
            >
              First
            </button>
            <button
              className="admin-btn admin-btn--secondary admin-btn--sm"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <span className="admin-data-table__pagination-current">
              Page {currentPage} of {totalPages}
            </span>
            <button
              className="admin-btn admin-btn--secondary admin-btn--sm"
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
            <button
              className="admin-btn admin-btn--secondary admin-btn--sm"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
            >
              Last
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;
